import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, Building2, Inbox, Landmark, Plus, Save, Scale, Trash2 } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import type { Cambios, Fuente } from "../api/tipos.ts";
import {
  usarConfiguracion,
  usarGuardarConfiguracion,
  usarSimularConfiguracion,
} from "../ganchos/datos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { Credenciales } from "./Credenciales.tsx";
import { Telegram } from "./Telegram.tsx";
import { ErrorConsulta } from "../componentes/Errores.tsx";
import { Aviso, Boton, Campo, Cargando, Dialogo, Seccion, Tarjeta, cx } from "../componentes/ui.tsx";
import {
  CLAVES,
  TIPOS_FUENTE,
  calcularCambios,
  escribirFuentes,
  fuenteVacia,
  hayCambios,
  leerFuentes,
  BANCOS_COMUNES,
  TIPOS_CUENTA,
  cuentaVacia,
  escribirCuentas,
  leerCuentas,
  type CuentaEditable,
} from "./configuracion-modelo.ts";

/**
 * La pantalla donde un cliente se configura solo (SPEC-017 §10).
 *
 * Dos decisiones gobiernan el archivo:
 *
 * **No valida nada.** Quien valida es el backend, con el mismo código que corre
 * el motor, y sus mensajes están escritos para leerse — «"x" no es un monto que
 * pueda interpretar. Usa por ejemplo 5000 o 5.000». Repetir esas reglas aquí
 * sería crear una segunda verdad que se separa de la primera en el primer
 * cambio; lo que hace esta pantalla es poner cada error en su campo.
 *
 * **Los avisos no se esconden.** `cargarConfiguracion` devuelve consecuencias
 * que no son errores y que hasta ahora no veía nadie. Un formulario que las
 * guarda en silencio y dice «Guardado» oculta lo único que había que decir.
 */
export function Configuracion() {
  const { data, isPending, error, refetch } = usarConfiguracion();
  const guardar = usarGuardarConfiguracion();
  const simular = usarSimularConfiguracion();
  const { rol } = usarSesion();

  // «Conectar Telegram» y «Conectar mi sistema», desde Empezar, enlazan a su
  // sección (SPEC-029 §15). El enrutador no baja solo a un ancla.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || isPending) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, isPending]);

  /** Lo que está guardado, indexado por clave. */
  const guardado = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const c of data?.claves ?? []) if (c.definida) mapa[c.clave] = c.valor;
    return mapa;
  }, [data]);

  const [editado, setEditado] = useState<Record<string, string>>({});
  const [fuentes, setFuentes] = useState<Fuente[]>([]);
  const [cuentas, setCuentas] = useState<CuentaEditable[]>([]);
  const [porConfirmar, setPorConfirmar] = useState<Cambios | null>(null);
  const [avisosTras, setAvisosTras] = useState<string[] | null>(null);

  // Al llegar los datos —y al recargarlos tras guardar— el formulario parte de
  // lo guardado. Sin esto, la pantalla seguiría mostrando lo que el usuario
  // escribió aunque el servidor hubiera guardado otra cosa.
  useEffect(() => {
    if (!data) return;
    setEditado({ ...guardado });
    setFuentes(leerFuentes(guardado[CLAVES.fuentes]));
    setCuentas(leerCuentas(guardado[CLAVES.cuentasBancarias]));
  }, [data, guardado]);

  const valor = (clave: string) => editado[clave] ?? "";
  const poner = (clave: string, v: string) => setEditado((e) => ({ ...e, [clave]: v }));

  /** El error del backend viene con la clave que lo causó: va en su campo. */
  const errorApi = guardar.error instanceof ErrorApi ? guardar.error : null;
  const claveConError = typeof errorApi?.datos.clave === "string" ? errorApi.datos.clave : null;
  const errorDe = (clave: string) => (claveConError === clave ? errorApi?.message : undefined);

  const cambios = useMemo(
    () =>
      calcularCambios(guardado, {
        ...editado,
        [CLAVES.fuentes]: escribirFuentes(fuentes),
        [CLAVES.cuentasBancarias]: escribirCuentas(cuentas),
      }),
    [guardado, editado, fuentes, cuentas],
  );

  function aplicar(c: Cambios) {
    guardar.mutate(c, { onSuccess: (r) => setAvisosTras(r.avisos) });
  }

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    setAvisosTras(null);
    if (!hayCambios(cambios)) return;

    // Donde hay algo que explicar, se explica ANTES (SPEC-017 §5): el servidor
    // valida y devuelve las consecuencias sin escribir nada.
    if (necesitaExplicacion(cambios)) {
      simular.mutate(cambios, { onSuccess: () => setPorConfirmar(cambios) });
      return;
    }
    aplicar(cambios);
  }

  if (isPending) return <Cargando texto="Cargando la configuración…" />;
  if (error) {
    if (error instanceof ErrorApi && error.esSinPermiso) {
      return (
        <Seccion titulo="Configuración">
          <Tarjeta className="p-6">
            <Aviso tono="aviso" titulo="Esto lo gestiona el dueño de la cuenta">
              Tu rol ({rol}) no puede cambiar la configuración. Pídeselo a quien sea dueño.
            </Aviso>
          </Tarjeta>
        </Seccion>
      );
    }
    return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;
  }

  return (
    <Seccion
      titulo="Configuración"
      descripcion="De dónde llega el correo de esta cuenta y cómo avisa."
    >
      <form className="space-y-6" onSubmit={enviar}>
        {/* Lo que el sistema ya está haciendo, aunque nadie se lo haya dicho. */}
        {!!data?.avisos.length && (
          <Aviso tono="aviso" titulo="Cómo está funcionando ahora" icono={AlertTriangle}>
            <ul className="ml-4 list-disc space-y-1">
              {data.avisos.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </Aviso>
        )}

        {errorApi && !claveConError && <Aviso titulo="No se pudo guardar">{errorApi.message}</Aviso>}

        {avisosTras !== null && (
          <Aviso
            tono={avisosTras.length ? "aviso" : "exito"}
            titulo={avisosTras.length ? "Guardado, y esto es lo que cambia" : "Guardado"}
          >
            {avisosTras.length ? (
              <ul className="ml-4 list-disc space-y-1">
                {avisosTras.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            ) : (
              "La configuración quedó al día."
            )}
          </Aviso>
        )}

        <Bloque
          icono={Inbox}
          titulo="Correo entrante"
          descripcion="Sin esto la cuenta rechaza todo lo que le llegue. Lo rechazado queda anotado en Recibidos, con el motivo."
        >
          <EditorFuentes fuentes={fuentes} alCambiar={setFuentes} error={errorDe(CLAVES.fuentes)} />
          <Campo
            etiqueta="Buzones autorizados a reenviar"
            ayuda="Buzones TUYOS que reenvían al de 4drando, separados por coma. Desde cualquier otro, un reenvío se rechaza. Las direcciones del banco no van aquí: esas ya entran directo por el campo de arriba."
            placeholder="dueno@tuempresa.com, caja@tuempresa.com"
            value={valor(CLAVES.reenviadores)}
            error={errorDe(CLAVES.reenviadores)}
            onChange={(e) => poner(CLAVES.reenviadores, e.target.value)}
          />
          <Campo
            etiqueta="Días que se guardan los correos rechazados"
            ayuda="Lo rechazado se ve en Recibidos y se borra pasado ese plazo. Lo que sí entró no se borra nunca por aquí. Entre 1 y 365; por defecto 30."
            inputMode="numeric"
            placeholder="30"
            value={valor(CLAVES.diasRecibidos)}
            error={errorDe(CLAVES.diasRecibidos)}
            onChange={(e) => poner(CLAVES.diasRecibidos, e.target.value)}
          />
        </Bloque>

        <div id="cuentas" className="scroll-mt-6">
          <Bloque
            icono={Landmark}
            titulo="Tus cuentas bancarias"
            descripcion="Las cuentas donde recibes pagos. Con ellas 4drando dice a cuál entró cada pago, aunque la alerta del banco no lo diga."
          >
            <EditorCuentas
              cuentas={cuentas}
              alCambiar={setCuentas}
              bancos={data?.bancos ?? []}
              porRegistrar={data?.cuentasPorRegistrar ?? []}
              error={errorDe(CLAVES.cuentasBancarias)}
            />
          </Bloque>
        </div>

        <Bloque icono={Building2} titulo="Avisos" descripcion="Por dónde suena y cuánto cuenta.">
          <Opciones
            etiqueta="Canal"
            valor={valor(CLAVES.canalAviso) || "registro"}
            alCambiar={(v) => poner(CLAVES.canalAviso, v)}
            opciones={[
              { valor: "registro", nombre: "Ninguno", ayuda: "Queda en el registro del servidor." },
              {
                valor: "telegram",
                nombre: "Telegram",
                ayuda: "Se enciende conectándolo más abajo, sin configurar nada.",
              },
            ]}
          />
          {/* El token y el chat a mano son la salida de emergencia, no el camino
              (SPEC-023 §3): solo se enseñan a quien YA tiene un bot propio
              guardado. A quien no, ofrecerle estos dos campos es ofrecerle el
              trámite que esta versión existe para quitarle. */}
          {valor(CLAVES.canalAviso) === "telegram" && guardado[CLAVES.telegramToken] && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                etiqueta="Token del bot"
                type="password"
                ayuda={
                  guardado[CLAVES.telegramToken]
                    ? "Ya hay uno guardado. Escribe otro para reemplazarlo, o bórralo para quitarlo."
                    : "El que te da @BotFather al crear el bot."
                }
                value={valor(CLAVES.telegramToken)}
                error={errorDe(CLAVES.telegramToken)}
                onChange={(e) => poner(CLAVES.telegramToken, e.target.value)}
              />
              <Campo
                etiqueta="Chat"
                ayuda="El identificador del chat o grupo donde debe escribir."
                value={valor(CLAVES.telegramChat)}
                error={errorDe(CLAVES.telegramChat)}
                onChange={(e) => poner(CLAVES.telegramChat, e.target.value)}
              />
            </div>
          )}
          <Opciones
            etiqueta="Cuánto dice el aviso"
            valor={valor(CLAVES.nivelAviso) || "minimo"}
            alCambiar={(v) => poner(CLAVES.nivelAviso, v)}
            opciones={[
              { valor: "minimo", nombre: "Lo mínimo", ayuda: "Que hay algo que mirar, y el enlace." },
              {
                valor: "completo",
                nombre: "Con detalle",
                ayuda: "Incluye monto y contraparte. Todo el que esté en ese chat los verá.",
              },
            ]}
          />
        </Bloque>

        <Bloque
          icono={Scale}
          titulo="Conciliación"
          descripcion="Con cuánto margen se da un pago por coincidente."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              etiqueta="Tolerancia en pesos"
              ayuda="Pagos que difieran menos que esto se dan por iguales en monto. Vacío: exacto."
              placeholder="0"
              value={valor(CLAVES.toleranciaPesos)}
              error={errorDe(CLAVES.toleranciaPesos)}
              onChange={(e) => poner(CLAVES.toleranciaPesos, e.target.value)}
            />
            <Campo
              etiqueta="Revisar desde"
              ayuda="Se ignora lo anterior a esta fecha. Útil al empezar."
              placeholder="01/08/2026"
              value={valor(CLAVES.revisarDesde)}
              error={errorDe(CLAVES.revisarDesde)}
              onChange={(e) => poner(CLAVES.revisarDesde, e.target.value)}
            />
          </div>
          <Campo
            etiqueta="Métodos de pago que no pasan por el banco"
            ayuda="Separados por coma. Sus abonos no esperan un movimiento bancario."
            placeholder="efectivo, canje"
            value={valor(CLAVES.metodosSinBanco)}
            error={errorDe(CLAVES.metodosSinBanco)}
            onChange={(e) => poner(CLAVES.metodosSinBanco, e.target.value)}
          />
        </Bloque>

        <div className="flex items-center justify-end gap-3">
          {hayCambios(cambios) && (
            <span className="text-sm text-tenue">
              {Object.keys(cambios).length} cambio(s) sin guardar
            </span>
          )}
          <Boton
            type="submit"
            variante="primario"
            icono={Save}
            cargando={guardar.isPending || simular.isPending}
            disabled={!hayCambios(cambios)}
          >
            Guardar
          </Boton>
        </div>
      </form>

      {/* Fuera del formulario a propósito: crear o revocar una credencial ocurre
          en el momento, y meterlo dentro haría que «Guardar» significara dos
          cosas distintas. */}
      <div id="telegram" className="mt-10 scroll-mt-6 border-t border-borde pt-8">
        <Telegram />
      </div>

      <div id="credenciales" className="mt-10 scroll-mt-6 border-t border-borde pt-8">
        <Credenciales />
      </div>

      <Dialogo
        abierto={porConfirmar !== null}
        alCerrar={() => setPorConfirmar(null)}
        titulo="Esto es lo que va a pasar"
      >
        <ul className="ml-4 list-disc space-y-2 text-sm text-tinta">
          {(simular.data?.avisos ?? []).map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
        <div className="mt-6 flex justify-end gap-3">
          <Boton type="button" onClick={() => setPorConfirmar(null)}>
            Cancelar
          </Boton>
          <Boton
            type="button"
            variante="primario"
            cargando={guardar.isPending}
            onClick={() => {
              const c = porConfirmar;
              setPorConfirmar(null);
              if (c) aplicar(c);
            }}
          >
            Entendido, guardar
          </Boton>
        </div>
      </Dialogo>
    </Seccion>
  );
}

/** Lo mismo que `exigeConfirmacion`, sobre el objeto ya calculado. */
const necesitaExplicacion = (c: Cambios) =>
  c[CLAVES.nivelAviso] === "completo" || (CLAVES.reglas in c && !!c[CLAVES.reglas]);

function Bloque({
  icono: Icono,
  titulo,
  descripcion,
  children,
}: {
  icono: typeof Inbox;
  titulo: string;
  descripcion: string;
  children: React.ReactNode;
}) {
  return (
    <Tarjeta className="space-y-5 p-6">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-xl bg-superficie-2 text-tenue">
          <Icono className="size-4.5" aria-hidden />
        </span>
        <div>
          <h3 className="text-sm font-semibold text-tinta">{titulo}</h3>
          <p className="text-sm text-tenue">{descripcion}</p>
        </div>
      </div>
      {children}
    </Tarjeta>
  );
}

function Opciones({
  etiqueta,
  valor,
  alCambiar,
  opciones,
}: {
  etiqueta: string;
  valor: string;
  alCambiar: (v: string) => void;
  opciones: { valor: string; nombre: string; ayuda: string }[];
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-tinta">{etiqueta}</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {opciones.map((o) => (
          <label
            key={o.valor}
            className={cx(
              "flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors",
              valor === o.valor
                ? "border-marca bg-marca/5"
                : "border-borde hover:border-borde-fuerte",
            )}
          >
            <input
              type="radio"
              className="mt-1 accent-[var(--color-marca)]"
              checked={valor === o.valor}
              onChange={() => alCambiar(o.valor)}
            />
            <span>
              <span className="block text-sm font-medium text-tinta">{o.nombre}</span>
              <span className="block text-xs text-tenue">{o.ayuda}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * El editor de fuentes.
 *
 * En la base esto es JSON, y **no se edita como JSON**: pedirle corchetes a un
 * tendero es trasladarle nuestro formato de almacenamiento. Aquí es una lista de
 * «quién manda el correo y desde qué dirección», que es lo que la persona sabe.
 */
function EditorFuentes({
  fuentes,
  alCambiar,
  error,
}: {
  fuentes: Fuente[];
  alCambiar: (f: Fuente[]) => void;
  error?: string;
}) {
  const cambiar = (i: number, parche: Partial<Fuente>) =>
    alCambiar(fuentes.map((f, j) => (i === j ? { ...f, ...parche } : f)));

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <span className="block text-sm font-medium text-tinta">Quién te manda correo</span>
          <span className="block text-xs text-tenue">
            Tu banco, y quien te emita las facturas. Solo se acepta correo de estas direcciones.
          </span>
        </div>
        <Boton
          type="button"
          icono={Plus}
          onClick={() => alCambiar([...fuentes, fuenteVacia()])}
        >
          Añadir
        </Boton>
      </div>

      {error && <Aviso titulo="Revisa las fuentes">{error}</Aviso>}

      {!fuentes.length && (
        <p className="rounded-xl border border-dashed border-borde p-4 text-center text-sm text-tenue">
          Todavía no hay ninguna. Sin al menos una, esta cuenta no puede recibir nada.
        </p>
      )}

      {fuentes.map((f, i) => (
        <div key={i} className="space-y-3 rounded-xl border border-borde bg-superficie-2 p-4">
          <div className="flex gap-3">
            <Campo
              etiqueta="Nombre"
              className="flex-1"
              placeholder="Bancolombia"
              value={f.nombre}
              onChange={(e) => cambiar(i, { nombre: e.target.value })}
            />
            <div className="w-44 space-y-1.5">
              <label className="block text-sm font-medium text-tinta">Tipo</label>
              <select
                className="h-11 w-full rounded-xl border border-borde bg-superficie px-3 text-sm text-tinta focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
                value={f.tipo}
                onChange={(e) => cambiar(i, { tipo: e.target.value as Fuente["tipo"] })}
              >
                {TIPOS_FUENTE.map((t) => (
                  <option key={t.valor} value={t.valor}>
                    {t.nombre}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              aria-label={`Quitar ${f.nombre || "esta fuente"}`}
              className="mt-7 grid size-11 shrink-0 place-items-center rounded-xl border border-borde text-tenue transition-colors hover:border-peligro/60 hover:text-peligro-tinta"
              onClick={() => alCambiar(fuentes.filter((_, j) => j !== i))}
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          </div>
          <Campo
            etiqueta="Direcciones desde las que escribe"
            ayuda="Separadas por coma. Son las direcciones del BANCO, las que él usa para mandar sus alertas. Lo que llegue desde una de ellas entra directo. Tu propio buzón no va aquí."
            placeholder="alertasynotificaciones@banco.com"
            value={f.remitentes.join(", ")}
            onChange={(e) => cambiar(i, { remitentes: e.target.value.split(",") })}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Las cuentas del negocio (SPEC-030 §4 y §4 bis). Arriba, los números que ya
 * trajeron las alertas y no están en la lista: quien configura no tiene que
 * sabérselos, solo agregarlos (P-2). El banco es libre, con sugerencias; el
 * tipo se elige siempre, porque la alerta no lo dice, y una Llave pide la llave
 * en vez de los últimos cuatro dígitos.
 */
function EditorCuentas({
  cuentas,
  alCambiar,
  bancos,
  porRegistrar,
  error,
}: {
  cuentas: CuentaEditable[];
  alCambiar: (c: CuentaEditable[]) => void;
  bancos: string[];
  porRegistrar: { banco: string; ultimos: string; pagos: number }[];
  error?: string;
}) {
  const cambiar = (i: number, parche: Partial<CuentaEditable>) =>
    alCambiar(cuentas.map((c, j) => (i === j ? { ...c, ...parche } : c)));
  // Las que ya se agregaron en esta edición, aunque no se hayan guardado, dejan
  // de ofrecerse.
  const ofrecer = porRegistrar.filter(
    (o) => !cuentas.some((c) => c.tipo !== "Llave" && c.ultimos === o.ultimos && c.banco.toLowerCase() === o.banco.toLowerCase()),
  );
  const select =
    "h-11 w-full rounded-xl border border-borde bg-superficie px-3 text-sm text-tinta focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none";
  // Primero los bancos de los que ya llegan alertas, luego los comunes.
  const sugeridos = [...new Set([...bancos, ...BANCOS_COMUNES])];
  const avisarNoLlegan = (banco: string) =>
    !!banco.trim() && !bancos.some((b) => b.toLowerCase() === banco.trim().toLowerCase());

  return (
    <div className="space-y-3">
      <datalist id="bancos-sugeridos">
        {sugeridos.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      {ofrecer.map((o) => (
        <div
          key={`${o.banco}|${o.ultimos}`}
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-marca/30 bg-marca-suave/40 p-3 text-sm"
        >
          <span className="text-tinta">
            {o.pagos === 1 ? "Llegó" : "Llegaron"} <span className="cifra font-semibold">{o.pagos}</span>{" "}
            {o.pagos === 1 ? "pago" : "pagos"} a la cuenta{" "}
            <span className="cifra font-semibold">*{o.ultimos}</span> de {o.banco}, y no está en tu lista.
          </span>
          <Boton type="button" tamano="sm" variante="suave" icono={Plus} onClick={() => alCambiar([...cuentas, cuentaVacia(o.banco, o.ultimos)])}>
            Agregarla
          </Boton>
        </div>
      ))}

      {error && <Aviso titulo="Revisa las cuentas">{error}</Aviso>}

      {!cuentas.length && !ofrecer.length && (
        <p className="rounded-xl border border-dashed border-borde p-4 text-center text-sm text-tenue">
          Todavía no hay ninguna. Sin ellas, los pagos cuya alerta no dice la cuenta quedan «sin identificar».
        </p>
      )}

      {cuentas.map((c, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-borde bg-superficie-2 p-4">
          <div className="grid gap-3 sm:grid-cols-[1.2fr_9rem_1.2fr_1.2fr_auto] sm:items-end">
            <Campo
              etiqueta="Banco"
              list="bancos-sugeridos"
              autoComplete="off"
              placeholder="Bancolombia, Nu…"
              value={c.banco}
              onChange={(e) => cambiar(i, { banco: e.target.value })}
            />
            <div className="space-y-1.5">
              <label htmlFor={`cuenta-tipo-${i}`} className="block text-sm font-medium text-tinta">Tipo</label>
              <select
                id={`cuenta-tipo-${i}`}
                className={select}
                value={c.tipo}
                onChange={(e) => cambiar(i, { tipo: e.target.value as CuentaEditable["tipo"] })}
              >
                <option value="" disabled>Elige…</option>
                {TIPOS_CUENTA.map((t) => (
                  <option key={t.valor} value={t.valor}>{t.nombre}</option>
                ))}
              </select>
            </div>
            {c.tipo === "Llave" ? (
              <Campo
                etiqueta="Llave"
                autoComplete="off"
                placeholder="@tunegocio, celular o correo"
                value={c.llave}
                onChange={(e) => cambiar(i, { llave: e.target.value.replace(/\s/g, "") })}
              />
            ) : (
              <Campo
                etiqueta="Termina en"
                inputMode="numeric"
                maxLength={4}
                placeholder="1592"
                value={c.ultimos}
                onChange={(e) => cambiar(i, { ultimos: e.target.value.replace(/\D/g, "") })}
              />
            )}
            <Campo
              etiqueta="Nombre"
              placeholder="Ahorros principal"
              value={c.nombre}
              onChange={(e) => cambiar(i, { nombre: e.target.value })}
            />
            <button
              type="button"
              aria-label={`Quitar ${c.nombre || "esta cuenta"}`}
              className="grid size-11 shrink-0 place-items-center rounded-xl border border-borde text-tenue transition-colors hover:border-peligro/60 hover:text-peligro-tinta"
              onClick={() => alCambiar(cuentas.filter((_, j) => j !== i))}
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          </div>
          {avisarNoLlegan(c.banco) && (
            <p className="text-xs text-tenue">
              4drando no recibe alertas de {c.banco.trim()}: sus pagos no aparecen solos. Esta cuenta sirve para elegirla a
              mano en un pago.
            </p>
          )}
        </div>
      ))}

      <Boton type="button" icono={Plus} onClick={() => alCambiar([...cuentas, cuentaVacia()])}>
        Añadir una cuenta
      </Boton>
    </div>
  );
}
